import { ORIGIN } from "../utils/configs";

const CAPTURE_SPECS = Object.freeze([
  Object.freeze({ timeframe: "15M", resolution: "15" }),
  Object.freeze({ timeframe: "4H", resolution: "240" }),
  Object.freeze({ timeframe: "1D", resolution: "1D" }),
]);

const normalizeSymbol = (value) => {
  const symbol = String(value || "").toUpperCase().replace(/[^A-Z0-9]/g, "");
  if (!/^[A-Z0-9]{1,20}USDT$/.test(symbol)) {
    throw new Error("Choose a valid USDT Futures symbol.");
  }
  return symbol;
};

const captureOneChart = (symbol, spec, timeoutMs, signal) => new Promise((resolve, reject) => {
  if (signal?.aborted) return reject(new Error("Chart capture cancelled."));
  const chartOrigin = new URL(ORIGIN).origin;
  const requestId = window.crypto?.randomUUID?.()
    || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  const url = new URL("/tvcharts", ORIGIN);
  url.searchParams.set("currency", symbol.slice(0, -4));
  url.searchParams.set("base", "USDT");
  url.searchParams.set("intervals", spec.resolution);
  url.searchParams.set("capture", "1");
  url.searchParams.set("market", "futures");
  url.searchParams.set("isSpot", "false");
  url.searchParams.set("theme", "dark");

  const iframe = document.createElement("iframe");
  iframe.title = `${spec.timeframe} chart capture`;
  iframe.src = url.toString();
  iframe.setAttribute("aria-hidden", "true");
  Object.assign(iframe.style, {
    position: "fixed",
    left: "-10000px",
    top: "0",
    width: "1200px",
    height: "700px",
    border: "0",
    opacity: "0.01",
    pointerEvents: "none",
    zIndex: "-2147483648",
  });

  let settled = false;
  const cleanup = () => {
    signal?.removeEventListener("abort", onAbort);
    window.removeEventListener("message", onMessage);
    iframe.remove();
  };
  const onAbort = () => finish(new Error("Chart capture cancelled."));
  const finish = (error, value) => {
    if (settled) return;
    settled = true;
    window.clearTimeout(timer);
    cleanup();
    if (error) reject(error);
    else resolve(value);
  };
  const onMessage = (event) => {
    if (event.origin !== chartOrigin || event.source !== iframe.contentWindow) return;
    const message = event.data;
    if (!message || typeof message !== "object") return;
    if (message.type === "chart_capture_ready") {
      iframe.contentWindow?.postMessage({
        type: "chart_capture_request",
        requestId,
        symbol,
        resolution: spec.resolution,
      }, chartOrigin);
      return;
    }
    if (message.requestId !== requestId) return;
    if (message.type === "chart_capture_error") {
      finish(new Error(message.error || `Could not capture current ${spec.timeframe} chart.`));
      return;
    }
    if (message.type !== "chart_capture_result") return;
    if (message.symbol !== symbol || String(message.resolution) !== spec.resolution) {
      finish(new Error(`Current ${spec.timeframe} chart did not match the requested market.`));
      return;
    }
    if (!/^data:image\/(?:jpeg|png);base64,[A-Za-z0-9+/=]+$/.test(String(message.dataUrl || ""))) {
      finish(new Error(`Current ${spec.timeframe} chart image is invalid.`));
      return;
    }
    const capturedAt = Number(message.capturedAt);
    const asOf = Number(message.asOf);
    if (!Number.isFinite(capturedAt) || capturedAt > Date.now() + 5_000 || Date.now() - capturedAt > 60_000
      || !Number.isFinite(asOf) || asOf * 1000 > capturedAt + 5_000 || capturedAt - asOf * 1000 > 30_000) {
      finish(new Error(`Current ${spec.timeframe} chart has stale or missing capture metadata.`));
      return;
    }
    finish(null, {
      timeframe: spec.timeframe,
      dataUrl: message.dataUrl,
      mimeType: message.mimeType || "image/jpeg",
      width: Number(message.width) || 1200,
      height: Number(message.height) || 700,
      capturedAt,
      asOf,
      latestBarTime: Number(message.latestBarTime),
      candleBasis: "live forming candle",
    });
  };
  const timer = window.setTimeout(() => {
    finish(new Error(`Current ${spec.timeframe} chart capture timed out.`));
  }, Math.max(10_000, Number(timeoutMs) || 45_000));

  window.addEventListener("message", onMessage);
  signal?.addEventListener("abort", onAbort, { once: true });
  document.body.appendChild(iframe);
});

export async function captureBrowserCharts({ symbol: rawSymbol, enabled = true, timeoutMs = 45_000, signal } = {}) {
  if (!enabled) return [];
  const symbol = normalizeSymbol(rawSymbol);
  const controller = new AbortController();
  let timedOut = false;
  const totalTimer = window.setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, Math.min(30_000, Math.max(10_000, Number(timeoutMs) || 30_000)));
  const abort = () => controller.abort();
  signal?.addEventListener("abort", abort, { once: true });
  if (signal?.aborted) controller.abort();
  let nextIndex = 0;
  const screenshots = new Array(CAPTURE_SPECS.length);
  try {
    await Promise.all(Array.from({ length: 2 }, async () => {
      while (nextIndex < CAPTURE_SPECS.length) {
        const index = nextIndex++;
        screenshots[index] = await captureOneChart(symbol, CAPTURE_SPECS[index], timeoutMs, controller.signal);
      }
    }));
    return screenshots;
  } catch (error) {
    if (timedOut) throw new Error("Current chart capture timed out. Please capture again.");
    throw error;
  } finally {
    window.clearTimeout(totalTimer);
    controller.abort();
    signal?.removeEventListener("abort", abort);
  }
}
